/**
 * The Schools table's Export button for Product Insights · LAE (Productivity, Schools mode).
 *
 * Ports from the LAE app (`src/features/productivity/`):
 *   - `ExportButton.jsx`: a secondary button, disabled when there is nothing to export;
 *   - `SchoolsView.jsx` `SchoolsExport`: flattens the table's rows before writing them.
 *
 * Deliberate differences from LAE (spec section 6):
 *   - The sheet is written by `exportSchoolsXlsx` (`./lae-export`, SEED EMERGE's lazy SheetJS
 *     writer), which flattens the rows itself, so nothing is memoised here.
 *   - LAE's hover description is dropped (no helper text). The only hover label is the reason the
 *     button is disabled, "No rows to export", which `Button` renders on its wrapper span.
 *   - A failure (SheetJS failed to load, the write threw) shows a danger `Notice` under the button;
 *     LAE had no error state.
 *   - Not gated on an export grant: no other Product Insights screen gates its exports, and the
 *     rows are already ones the viewer can read.
 *   - `rows` must be the table's FILTERED and SORTED rows (C8 `actions(visibleRows)`), as in LAE.
 */
import { useState } from 'react';
import { Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { exportSchoolsXlsx } from './lae-export';
import type { ExportSchoolsXlsxOptions } from './lae-export';
import type { LaeProductivityParams } from './lae-params';

export interface SchoolsExportButtonProps {
  /** The table's visible rows, filtered and sorted. */
  rows: ExportSchoolsXlsxOptions['rows'];
  /** The person-deduplicated grand totals; written only when every group is shown. */
  totals: ExportSchoolsXlsxOptions['totals'];
  /** How many school × funnel groups the response holds, before table filters. */
  groupCount: number;
  /** Split by assignment (one sheet row per assignment line) or one line per school. */
  split: boolean;
  /** The page's params (`useLaeProductivityParams()`); only the period, cycle and source are read. */
  params: Pick<LaeProductivityParams, 'range' | 'cycle' | 'source'>;
  /** The funnel picked in the controls bar; null or empty = All funnels. */
  funnelLabel?: string | null;
}

export function SchoolsExportButton({
  rows,
  totals,
  groupCount,
  split,
  params,
  funnelLabel,
}: SchoolsExportButtonProps) {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const empty = rows.length === 0;

  async function handleExport() {
    if (empty || exporting) return;
    setExporting(true);
    setError(null);
    try {
      await exportSchoolsXlsx({
        rows,
        totals,
        groupCount,
        split,
        from: params.range.from,
        to: params.range.to,
        cycle: params.cycle,
        funnelLabel,
        source: params.source,
      });
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? `The export could not be created: ${err.message}`
          : 'The export could not be created.',
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <Button
        variant="secondary"
        loading={exporting}
        disabled={empty}
        tooltip={empty ? 'No rows to export' : undefined}
        onClick={() => void handleExport()}
      >
        Export
      </Button>
      {error !== null && (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}
    </div>
  );
}
